import { useMemo } from 'react';

import type {
	FunnelStage,
	StrategicDirection,
	StrategyCampaignProposal,
	StrategyDefinition,
	StrategyMetric,
	Strategy,
} from '../../../../bridge/types/strategy.ts';

import {
	canonicalGlossSites,
	normalizePlanMetric,
	selectPrimaryMetric,
} from './strategyFormat';

export interface OrderedMetric {
	metric: StrategyMetric;
	rawIndex: number;
	variant: 'primary' | 'supporting';
}

export interface StrategyPlanView {
	strategy: StrategicDirection | null;
	campaigns: StrategyCampaignProposal[];
	metrics: StrategyMetric[];
	definitions?: StrategyDefinition[];
	glossSites: Map<string, string>;
	funnelStages: FunnelStage[];
	orderedMetrics: OrderedMetric[];
}

export function useStrategyPlanView(run: Strategy | null): StrategyPlanView {
	const strategy = run?.direction ?? null;
	const rawCampaigns = run?.campaign_proposals;
	const rawMetrics = strategy?.metrics;

	const campaigns = useMemo(() => rawCampaigns ?? [], [rawCampaigns]);

	const glossSites = useMemo(
		() =>
			canonicalGlossSites(
				strategy ? { ...strategy, statement: '' } : undefined,
				campaigns,
			),
		[strategy, campaigns],
	);

	const funnelStages =
		strategy?.funnelStages ??
		(strategy as { stages?: FunnelStage[] } | null)?.stages ??
		[];

	const orderedMetrics = useMemo<OrderedMetric[]>(() => {
		const ms = (rawMetrics ?? []).map((m, rawIndex) => ({
			metric: normalizePlanMetric(m as unknown as Record<string, unknown>),
			rawIndex,
		}));
		if (ms.length <= 1) {
			return ms.map(({ metric, rawIndex }) => ({
				metric,
				rawIndex,
				variant: 'primary' as const,
			}));
		}
		const primaryIdx = selectPrimaryMetric(rawMetrics)?.rawIndex ?? 0;
		return [
			{ ...ms[primaryIdx], variant: 'primary' as const },
			...ms
				.filter((_, i) => i !== primaryIdx)
				.map((entry) => ({ ...entry, variant: 'supporting' as const })),
		];
	}, [rawMetrics]);

	return {
		strategy,
		campaigns,
		metrics: rawMetrics ?? [],
		definitions: strategy?.definitions,
		glossSites,
		funnelStages,
		orderedMetrics,
	};
}
